import { useState, useEffect, useCallback } from "react";



import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const API = "https://gipl-backend.onrender.com/api/v1";

// ─── APIFETCH HELPER (for policy forms) ──────────────────────────────────────
const apiFetch = async (method, path, body, token) => {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
};

// ─── ROLE NORMALIZER ─────────────────────────────────────────────────────────
const normalizeRole = (role) => role ? role.replace(/_/g, ' ') : '';

const COLORS = {
  gold: "#C9A84C", goldLight: "#E8C97A",
  navy: "#0A1628", navyMid: "#132240", navyLight: "#1E3459",
  cream: "#F8F4ED", success: "#22C55E", warning: "#F59E0B",
  danger: "#EF4444", info: "#3B82F6"
};

// ─── API HELPER ───────────────────────────────────────────────────────────────
const api = {
  get: async (path, token) => {
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    });
    if (res.status === 401) throw new Error("UNAUTHORIZED");
    return res.json();
  },
  post: async (path, body, token) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return res.json();
  }
};

// ─── AUTH CONTEXT ─────────────────────────────────────────────────────────────
let globalToken = localStorage.getItem("gipl_token") || "";
let globalUser  = JSON.parse(localStorage.getItem("gipl_user") || "null");

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120 }}>
    <div style={{ width: 32, height: 32, border: `3px solid ${COLORS.cream}`, borderTopColor: COLORS.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const EmptyState = ({ icon, title, sub }) => (
  <div style={{ textAlign: "center", padding: "40px 20px", color: "#718096" }}>
    <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.navy, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 13 }}>{sub}</div>
  </div>
);

const Badge = ({ status }) => {
  status = normalizeRole(status);
  const map = {
    Active: { bg: "#dcfce7", color: "#15803d" }, "Due Soon": { bg: "#fef9c3", color: "#a16207" },
    Expired: { bg: "#fee2e2", color: "#b91c1c" }, Lapsed: { bg: "#ffe4e6", color: "#be123c" },
    Verified: { bg: "#dcfce7", color: "#15803d" }, Pending: { bg: "#fef9c3", color: "#a16207" },
    Rejected: { bg: "#fee2e2", color: "#b91c1c" }, Inactive: { bg: "#f1f5f9", color: "#64748b" },
    "Super Admin": { bg: "#ede9fe", color: "#6d28d9" }, "Branch Admin": { bg: "#dbeafe", color: "#1d4ed8" },
    "Principal Officer": { bg: "#d1fae5", color: "#065f46" }, "ISP": { bg: "#fef9c3", color: "#a16207" },
  };
  const s = map[status] || { bg: "#f1f5f9", color: "#64748b" };
  return <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20 }}>{status}</span>;
};

const KPICard = ({ label, value, sub, icon, color }) => (
  <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, padding: "20px 22px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
      <span style={{ fontSize: 12, color: "#718096", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 22 }}>{icon}</span>
    </div>
    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: color || COLORS.navy, lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#718096" }}>{sub}</div>}
  </div>
);

const Table = ({ cols, rows, renderRow, loading }) => (
  <div style={{ overflowX: "auto" }}>
    {loading ? <Spinner /> : (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8f4ed" }}>
            {cols.map(c => <th key={c} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#718096", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={cols.length}><EmptyState icon="📭" title="No records found" sub="Data will appear here once added" /></td></tr>
            : rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1ebe0" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fdfaf5"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                {renderRow(row)}
              </tr>
            ))}
        </tbody>
      </table>
    )}
  </div>
);

const TD = ({ children, bold }) => (
  <td style={{ padding: "12px 14px", color: bold ? COLORS.navy : "#4a5568", fontWeight: bold ? 600 : 400, whiteSpace: "nowrap" }}>{children}</td>
);

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
const LoginPage = ({ onLogin }) => {
  const [email, setEmail]       = useState("admin@gipl.in");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const data = await api.post("/auth/login", { email, password });
      if (data.success) {
        localStorage.setItem("gipl_token", data.token);
        const normalizedUser = {...data.user, role: data.user.role ? data.user.role.replace(/_/g,' ') : data.user.role};
        localStorage.setItem("gipl_user", JSON.stringify(normalizedUser));
        onLogin(data.token, data.user);
      } else {
        setError(data.message || "Login failed. Check email and password.");
      }
    } catch (err) {
      setError("Cannot connect to server. Make sure npm run dev is running on port 5000.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.navy, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap')`}</style>
      <div style={{ width: "100%", maxWidth: 420, padding: "0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 64, height: 64, background: `linear-gradient(135deg,${COLORS.gold},${COLORS.goldLight})`, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: COLORS.navy }}>G</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 4 }}>GIPL Insurance</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", letterSpacing: "0.1em", textTransform: "uppercase" }}>CRM Portal — Staff Login</div>
        </div>

        {/* Card */}
        <div style={{ background: "#fff", borderRadius: 20, padding: 32, boxShadow: "0 24px 60px rgba(0,0,0,0.4)" }}>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#718096", marginBottom: 6, letterSpacing: "0.04em", textTransform: "uppercase" }}>Email Address</label>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required
                style={{ width: "100%", padding: "11px 14px", border: "1px solid #e8e2d6", borderRadius: 10, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#fdfaf5" }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#718096", marginBottom: 6, letterSpacing: "0.04em", textTransform: "uppercase" }}>Password</label>
              <input value={password} onChange={e => setPassword(e.target.value)} type="password" required
                style={{ width: "100%", padding: "11px 14px", border: "1px solid #e8e2d6", borderRadius: 10, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#fdfaf5" }} />
            </div>
            {error && <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: COLORS.danger, marginBottom: 16, lineHeight: 1.5 }}>{error}</div>}
            <button type="submit" disabled={loading}
              style={{ width: "100%", padding: "13px", background: `linear-gradient(135deg,${COLORS.navy},${COLORS.navyLight})`, color: COLORS.gold, border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", letterSpacing: "0.02em" }}>
              {loading ? "Signing in..." : "Sign In to GIPL"}
            </button>
          </form>
          <div style={{ marginTop: 20, padding: "12px 14px", background: "#f8f4ed", borderRadius: 8, fontSize: 12, color: "#718096", lineHeight: 1.7, textAlign: "center" }}>
            🛡️ IRDAI Registered Insurance Marketing Firm<br />
            Guttikonda IMF Private Limited — Vijayawada
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── DASHBOARD MODULE ─────────────────────────────────────────────────────────
const DashboardModule = ({ token }) => {
  const [kpis, setKpis]       = useState(null);
  const [trend, setTrend]     = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [k, t, r] = await Promise.all([
          api.get("/dashboard/kpis", token),
          api.get("/dashboard/monthly-trend", token),
          api.get("/reminders?days=30&status=Pending", token),
        ]);
        if (k.success) setKpis(k.data);
        if (t.success) setTrend(t.data || []);
        if (r.success) setRenewals(r.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  if (loading) return <Spinner />;

  const policyMix = [
    { name: "Life", value: kpis?.totalLife || 0, color: COLORS.gold },
    { name: "Health", value: kpis?.totalHealth || 0, color: COLORS.info },
    { name: "General", value: kpis?.totalGeneral || 0, color: COLORS.success },
  ];
  const total = policyMix.reduce((s, p) => s + p.value, 0) || 1;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        <KPICard icon="📋" label="Total Policies" value={(kpis?.totalPolicies || 0).toLocaleString()} sub="All active segments" />
        <KPICard icon="👥" label="Total Customers" value={(kpis?.totalCustomers || 0).toLocaleString()} sub="Registered in CRM" />
        <KPICard icon="🧑‍💼" label="Active Agents" value={(kpis?.activeAgents || 0).toLocaleString()} sub="Currently active" />
        <KPICard icon="💰" label="Premium (Month)" value={`₹${((kpis?.premiumThisMonth || 0)/100000).toFixed(1)}L`} sub="Collected this month" color={COLORS.success} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 20 }}>
        <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, padding: 22 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy, marginBottom: 16 }}>Monthly Premium Trend (₹ Lakhs)</div>
          {trend.length === 0
            ? <EmptyState icon="📈" title="No payment data yet" sub="Premium payments will appear here once recorded" />
            : <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={trend}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.3}/><stop offset="95%" stopColor={COLORS.gold} stopOpacity={0}/></linearGradient></defs>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="premium" stroke={COLORS.gold} strokeWidth={2.5} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>}
        </div>
        <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, padding: 22 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy, marginBottom: 14 }}>Policy Mix</div>
          {total === 1
            ? <EmptyState icon="🥧" title="No policies yet" sub="Policy distribution appears here" />
            : <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart><Pie data={policyMix} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ name, value }) => `${name}: ${value}`} fontSize={11}>
                    {policyMix.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie><Tooltip /></PieChart>
                </ResponsiveContainer>
                {policyMix.map(p => (
                  <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#4a5568", marginTop: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: p.color }} />
                    {p.name} — {p.value} ({Math.round(p.value/total*100)}%)
                  </div>
                ))}
              </>}
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, padding: 22 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy, marginBottom: 14 }}>🔔 Upcoming Renewals (Next 30 Days)</div>
        {renewals.length === 0
          ? <EmptyState icon="🎉" title="No renewals due" sub="All policies are up to date for the next 30 days" />
          : renewals.slice(0, 6).map((r, i) => {
              const days = r.days_left ? Math.floor(r.days_left) : 0;
              const bg = days <= 7 ? "#fff5f5" : days <= 15 ? "#fffbeb" : "#f8f4ed";
              const dc = days <= 7 ? COLORS.danger : days <= 15 ? COLORS.warning : COLORS.success;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: bg, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: dc, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{r.customer || r.full_name}</div>
                    <div style={{ fontSize: 11, color: "#718096" }}>{r.type} · {r.policy_number} · ₹{Number(r.premium || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: dc }}>{days}d</div>
                </div>
              );
            })}
      </div>
    </div>
  );
};

// ─── CUSTOMERS MODULE ─────────────────────────────────────────────────────────
const CustomersModule = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [showAdd, setShowAdd]     = useState(false);
  const [form, setForm]           = useState({ full_name: "", mobile_primary: "", email: "", city: "", kyc_status: "Pending" });
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/customers?search=${encodeURIComponent(search)}&page=${page}&limit=10`, token);
      if (data.success) { setCustomers(data.data); setTotal(data.total); }
    } finally { setLoading(false); }
  }, [search, page, token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault(); setSaving(true); setMsg("");
    try {
      const data = await api.post("/customers", form, token);
      if (data.success) { setMsg("✅ Customer added successfully! Customer Code: " + data.data.customer_code); setForm({ full_name: "", mobile_primary: "", email: "", city: "", kyc_status: "Pending" }); load(); }
      else setMsg("❌ " + (data.message || "Failed to add customer"));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍  Search by name, mobile, city..."
          style={{ flex: 1, minWidth: 200, padding: "10px 14px", border: "1px solid #e8e2d6", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fdfaf5" }} />
        <button onClick={() => setShowAdd(!showAdd)}
          style={{ background: `linear-gradient(135deg,${COLORS.gold},${COLORS.goldLight})`, color: COLORS.navy, border: "none", padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          {showAdd ? "✕ Cancel" : "+ Add Customer"}
        </button>
      </div>

      {showAdd && (
        <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, padding: 22, marginBottom: 20 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy, marginBottom: 16 }}>Add New Customer</div>
          <form onSubmit={handleAdd}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
              {[["full_name","Full Name","text",true],["mobile_primary","Mobile Number","tel",true],["email","Email","email",false],["city","City","text",false]].map(([k,l,t,r]) => (
                <div key={k}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#718096", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{l}{r?" *":""}</label>
                  <input value={form[k]} onChange={e => setForm({...form,[k]:e.target.value})} type={t} required={r}
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e2d6", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#718096", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>KYC Status</label>
                <select value={form.kyc_status} onChange={e => setForm({...form,kyc_status:e.target.value})}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e2d6", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff", boxSizing: "border-box" }}>
                  <option>Pending</option><option>Verified</option><option>Rejected</option>
                </select>
              </div>
            </div>
            {msg && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, background: msg.startsWith("✅") ? "#f0fdf4" : "#fff5f5", color: msg.startsWith("✅") ? "#15803d" : COLORS.danger }}>{msg}</div>}
            <button type="submit" disabled={saving}
              style={{ background: COLORS.navy, color: COLORS.gold, border: "none", padding: "10px 24px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {saving ? "Saving..." : "Save Customer"}
            </button>
          </form>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, overflow: "hidden", padding: "20px 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy }}>Customer Database</div>
          <div style={{ fontSize: 13, color: "#718096" }}>{total} total records</div>
        </div>
        <Table loading={loading}
          cols={["Customer ID","Full Name","Mobile","City","KYC Status","Joined"]}
          rows={customers}
          renderRow={row => [
            <TD key="id"><span style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.gold, fontWeight: 600 }}>{row.customer_code}</span></TD>,
            <TD key="n" bold>{row.full_name}</TD>,
            <TD key="m">{row.mobile_primary}</TD>,
            <TD key="c">{row.city || "—"}</TD>,
            <TD key="k"><Badge status={row.kyc_status} /></TD>,
            <TD key="d">{row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN") : "—"}</TD>,
          ]}
        />
        {total > 10 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
              style={{ padding: "6px 14px", border: "1px solid #e8e2d6", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13 }}>← Prev</button>
            <span style={{ padding: "6px 14px", fontSize: 13, color: "#718096" }}>Page {page} of {Math.ceil(total/10)}</span>
            <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(total/10)}
              style={{ padding: "6px 14px", border: "1px solid #e8e2d6", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── POLICIES MODULE (Life / Health / General) ───────────────────────────────
const PoliciesModule = ({ token, type }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);

  const paths = { life: "/life", health: "/health", general: "/general" };
  const icons = { life: "💼", health: "🏥", general: "🚗" };
  const titles = { life: "Life Insurance Policies", health: "Health Insurance Policies", general: "General Insurance Policies" };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get(paths[type], token);
        if (data.success) { setPolicies(data.data || []); setTotal(data.total || 0); }
      } finally { setLoading(false); }
    };
    load();
  }, [type, token]);

  const colsMap = {
    life:    ["Policy No.", "Customer", "Company", "Sum Assured", "Annual Premium", "Next Due Date", "Status"],
    health:  ["Policy No.", "Customer", "Company", "Sum Insured", "Annual Premium", "Expiry Date", "Status"],
    general: ["Policy No.", "Customer", "Company", "Nature", "Sum Insured", "Annual Premium", "Expiry Date", "Status"],
  };

  const renderRow = (row) => {
    const cust = row.customers?.full_name || "—";
    const comp = row.insurance_companies?.company_name || "—";
    const prem = `₹${Number(row.annual_premium || 0).toLocaleString("en-IN")}`;
    const fmt  = (d) => d ? new Date(d).toLocaleDateString("en-IN") : "—";
    const si   = `₹${Number(row.sum_assured || row.sum_insured || 0).toLocaleString("en-IN")}`;

    const mono = (v) => <span style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.gold, fontWeight: 600 }}>{v}</span>;

    if (type === "life") return [
      <TD key="id">{mono(row.policy_number)}</TD>, <TD key="c" bold>{cust}</TD>, <TD key="co">{comp}</TD>,
      <TD key="sa" bold>{si}</TD>, <TD key="p">{prem}</TD>,
      <TD key="d" style={{ color: COLORS.warning }}>{fmt(row.next_premium_due_date)}</TD>,
      <TD key="s"><Badge status={row.policy_status} /></TD>,
    ];
    if (type === "health") return [
      <TD key="id">{mono(row.policy_number)}</TD>, <TD key="c" bold>{cust}</TD>, <TD key="co">{comp}</TD>,
      <TD key="sa" bold>{si}</TD>, <TD key="p">{prem}</TD>,
      <TD key="e">{fmt(row.policy_expiry_date)}</TD>,
      <TD key="s"><Badge status={row.policy_status} /></TD>,
    ];
    return [
      <TD key="id">{mono(row.policy_number)}</TD>, <TD key="c" bold>{cust}</TD>, <TD key="co">{comp}</TD>,
      <TD key="n">{row.policy_nature || "—"}</TD>, <TD key="sa" bold>{si}</TD>, <TD key="p">{prem}</TD>,
      <TD key="e">{fmt(row.policy_expiry_date)}</TD>,
      <TD key="s"><Badge status={row.policy_status} /></TD>,
    ];
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, overflow: "hidden", padding: "20px 22px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy }}>{icons[type]} {titles[type]}</div>
        <div style={{ fontSize: 13, color: "#718096" }}>{total} policies</div>
      </div>
      <Table loading={loading} cols={colsMap[type]} rows={policies} renderRow={renderRow} />
    </div>
  );
};

// ─── AGENTS MODULE ────────────────────────────────────────────────────────────
const AgentsModule = ({ token }) => {
  const [agents, setAgents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get("/agents", token);
        if (data.success) { setAgents(data.data || []); setTotal(data.total || 0); }
      } finally { setLoading(false); }
    };
    load();
  }, [token]);

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 14, overflow: "hidden", padding: "20px 22px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: COLORS.navy }}>🧑‍💼 Agents & Lead Generators</div>
        <div style={{ fontSize: 13, color: "#718096" }}>{total} agents</div>
      </div>
      <Table loading={loading}
        cols={["Agent Code","Name","Mobile","City","Policies Sold","Total Premium","Commission","Status"]}
        rows={agents}
        renderRow={row => [
          <TD key="id"><span style={{ fontFamily: "monospace", fontSize: 11, color: COLORS.gold, fontWeight: 600 }}>{row.agent_code}</span></TD>,
          <TD key="n" bold>{row.full_name}</TD>,
          <TD key="m">{row.mobile_primary}</TD>,
          <TD key="c">{row.city || "—"}</TD>,
          <TD key="p">{row.total_policies_sold}</TD>,
          <TD key="tp" bold>₹{Number(row.total_premium_generated || 0).toLocaleString("en-IN")}</TD>,
          <TD key="cm" bold style={{ color: COLORS.success }}>₹{Number(row.total_commission_earned || 0).toLocaleString("en-IN")}</TD>,
          <TD key="s"><Badge status={row.agent_status} /></TD>,
        ]}
      />
    </div>
  );
};


// ─── REMINDERS MODULE ─────────────────────────────────────────────────────────
const RemindersModule = ({ token }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(null);
  const [msg, setMsg]             = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/reminders?days=30", token);
      if (data.success) setReminders(data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSend = async (id, channel) => {
    setSending(id); setMsg("");
    try {
      const data = await api.post(`/reminders/${id}/send`, { channel }, token);
      if (data.success) { setMsg(`✅ Reminder sent via ${channel}!`); load(); }
      else setMsg("❌ Failed: " + data.message);
    } finally { setSending(null); setTimeout(() => setMsg(""), 3000); }
  };

  const critical = reminders.filter(r => Number(r.days_left || 0) <= 7).length;
  const dueSoon  = reminders.filter(r => { const d = Number(r.days_left || 0); return d > 7 && d <= 15; }).length;
  const upcoming = reminders.filter(r => Number(r.days_left || 0) > 15).length;

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
        <KPICard icon="🔴" label="Critical (≤7 days)" value={critical} sub="Immediate action needed" color={COLORS.danger} />
        <KPICard icon="🟡" label="Due Soon (8–15 days)" value={dueSoon} sub="Send reminders now" color={COLORS.warning} />
        <KPICard icon="🟢" label="Upcoming (16–30 days)" value={upcoming} sub="Schedule reminders" color={COLORS.success} />
      </div>
      {msg && <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:12, fontSize:13, background:msg.startsWith("✅")?"#f0fdf4":"#fff5f5", color:msg.startsWith("✅")?"#15803d":COLORS.danger }}>{msg}</div>}
      <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:22 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:COLORS.navy, marginBottom:14 }}>🔔 Renewal Reminders Queue</div>
        {loading ? <Spinner /> : reminders.length === 0
          ? <EmptyState icon="🎉" title="No pending reminders" sub="All renewals are handled" />
          : reminders.map((r, i) => {
              const days = Number(r.days_left || 0);
              const bg = days <= 7 ? "#fff5f5" : days <= 15 ? "#fffbeb" : "#f8f4ed";
              const dc = days <= 7 ? COLORS.danger : days <= 15 ? COLORS.warning : COLORS.success;
              const cust = r.customers?.full_name || r.customer || "—";
              const mob  = r.customers?.mobile_primary || "—";
              return (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:bg, borderRadius:10, marginBottom:10 }}>
                  <div style={{ width:44, height:44, borderRadius:"50%", background:bg, border:`2px solid ${dc}`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:14, color:dc, flexShrink:0 }}>{Math.floor(days)}d</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:COLORS.navy }}>{cust}</div>
                    <div style={{ fontSize:12, color:"#718096" }}>{r.policy_number} · {r.reminder_type} · ₹{Number(r.premium_amount || 0).toLocaleString()}</div>
                    <div style={{ fontSize:12, color:"#718096" }}>📞 {mob}</div>
                  </div>
                  <div style={{ display:"flex", gap:7 }}>
                    {["WhatsApp","Email","Call"].map(ch => (
                      <button key={ch} onClick={() => handleSend(r.reminder_id, ch)} disabled={sending === r.reminder_id}
                        style={{ padding:"6px 10px", borderRadius:7, fontSize:11, fontWeight:600, cursor:"pointer", border:"1px solid #e8e2d6", background:"#fff", color:"#4a5568" }}>
                        {ch==="WhatsApp"?"📱":ch==="Email"?"✉️":"📞"} {ch}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
};

// ─── REPORTS MODULE ───────────────────────────────────────────────────────────
const ReportsModule = ({ token }) => {
  const reports = [
    ["📋","Renewals Pending","/reports/renewals-pending?days=30"],
    ["🧑‍💼","Agent Performance","/reports/agent-performance"],
    ["💰","Premium Collection","/reports/premium-collection?from=2025-01-01&to=2025-12-31"],
    ["📊","Company Business","/reports/company-business"],
  ];

  const [loading, setLoading] = useState(null);
  const [results, setResults] = useState({});

  const fetchReport = async (path, name) => {
    setLoading(name);
    try {
      const data = await api.get(path, token);
      if (data.success) setResults(r => ({ ...r, [name]: data.data }));
    } finally { setLoading(null); }
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
      {reports.map(([icon, title, path]) => (
        <div key={title} style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:12, padding:20 }}>
          <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
          <div style={{ fontSize:14, fontWeight:600, color:COLORS.navy, marginBottom:6 }}>{title}</div>
          <button onClick={() => fetchReport(path, title)} disabled={loading === title}
            style={{ background:COLORS.navy, color:COLORS.gold, border:"none", padding:"8px 18px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", marginTop:8 }}>
            {loading === title ? "Loading..." : "📊 Fetch Report"}
          </button>
          {results[title] && (
            <div style={{ marginTop:12, background:"#f8f4ed", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#4a5568", maxHeight:120, overflowY:"auto" }}>
              <pre style={{ margin:0, whiteSpace:"pre-wrap", wordBreak:"break-all" }}>{JSON.stringify(results[title], null, 2).slice(0,400)}...</pre>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── USERS MODULE (Full CRUD) ─────────────────────────────────────────────────
const UsersModule = ({ token }) => {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm]         = useState({ full_name:"", email:"", password:"", role:"ISP", mobile:"" });
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const BASE = "https://gipl-backend.onrender.com/api/v1";

  const req = async (method, path, body) => {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json();
  };

  const load = async () => {
    setLoading(true);
    try { const d = await req("GET", "/users"); if (d.success) setUsers(d.data || []); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditUser(null);
    setForm({ full_name:"", email:"", password:"Admin@123", role:"ISP", mobile:"" });
    setMsg(""); setShowForm(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ full_name:u.full_name, email:u.email, password:"", role:u.role, mobile:u.mobile||"" });
    setMsg(""); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setMsg("");
    try {
      let d;
      if (editUser) {
        const body = { full_name:form.full_name, role:form.role, mobile:form.mobile };
        if (form.password) body.password = form.password;
        d = await req("PUT", `/users/${editUser.user_id}`, body);
      } else {
        d = await req("POST", "/users", form);
      }
      if (d.success) {
        setMsg(editUser ? "✅ User updated!" : "✅ User created!");
        setTimeout(() => { setShowForm(false); load(); }, 1200);
      } else setMsg("❌ " + (d.message || "Failed"));
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (u) => {
    const d = await req("DELETE", `/users/${u.user_id}`);
    if (d.success) { setConfirmDel(null); load(); }
    else alert("❌ " + d.message);
  };

  const inp = { width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", background:"#fdfaf5" };
  const lbl = { display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.04em" };

  return (
    <div>
      {confirmDel && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", borderRadius:16, padding:28, maxWidth:380, width:"90%", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:18, fontWeight:700, color:COLORS.navy, marginBottom:10 }}>⚠️ Deactivate User?</div>
            <div style={{ fontSize:14, color:"#4a5568", marginBottom:20, lineHeight:1.6 }}>
              Deactivate <strong>{confirmDel.full_name}</strong>? They will not be able to login.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => handleDeactivate(confirmDel)}
                style={{ background:COLORS.danger, color:"#fff", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Yes, Deactivate
              </button>
              <button onClick={() => setConfirmDel(null)}
                style={{ background:"#f8f4ed", border:"1px solid #e8e2d6", color:"#4a5568", padding:"9px 20px", borderRadius:8, fontSize:13, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <button onClick={openAdd}
          style={{ background:`linear-gradient(135deg,${COLORS.gold},${COLORS.goldLight})`, color:COLORS.navy, border:"none", padding:"10px 20px", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          + Add User
        </button>
      </div>

      {showForm && (
        <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:24, marginBottom:20 }}>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:COLORS.navy, marginBottom:16 }}>
            {editUser ? "✏️ Edit Staff Member" : "➕ Create New Staff Login"}
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:16 }}>
              <div>
                <label style={lbl}>Full Name *</label>
                <input value={form.full_name} onChange={e=>setForm({...form,full_name:e.target.value})} required style={inp} />
              </div>
              <div>
                <label style={lbl}>Email *</label>
                <input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} type="email" required={!editUser} disabled={!!editUser}
                  style={{...inp, background:editUser?"#f1f5f9":"#fdfaf5", color:editUser?"#718096":"inherit"}} />
              </div>
              <div>
                <label style={lbl}>{editUser?"New Password (blank = no change)":"Password *"}</label>
                <input value={form.password} onChange={e=>setForm({...form,password:e.target.value})} type="password" required={!editUser} style={inp}
                  placeholder={editUser?"Leave blank to keep current":""} />
              </div>
              <div>
                <label style={lbl}>Mobile</label>
                <input value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value})} type="tel" style={inp} />
              </div>
              <div>
                <label style={lbl}>Role *</label>
                <select value={form.role} onChange={e=>setForm({...form,role:e.target.value})} style={{...inp}}>
                  <option>Super Admin</option><option>Branch Admin</option><option>Principal Officer</option><option>ISP</option>
                </select>
              </div>
            </div>
            {msg && <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:12, fontSize:13, background:msg.startsWith("✅")?"#f0fdf4":"#fff5f5", color:msg.startsWith("✅")?"#15803d":COLORS.danger }}>{msg}</div>}
            <div style={{ display:"flex", gap:10 }}>
              <button type="submit" disabled={saving}
                style={{ background:COLORS.navy, color:COLORS.gold, border:"none", padding:"10px 24px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                {saving?"Saving...":editUser?"💾 Update User":"✅ Create User"}
              </button>
              <button type="button" onClick={()=>setShowForm(false)}
                style={{ background:"#f8f4ed", border:"1px solid #e8e2d6", color:"#4a5568", padding:"10px 20px", borderRadius:8, fontSize:13, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, overflow:"hidden", padding:"20px 22px" }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:COLORS.navy, marginBottom:16 }}>👤 System Users — Staff Logins</div>
        {loading ? <div style={{textAlign:"center",padding:40,color:"#718096"}}>Loading...</div> : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8f4ed" }}>
                  {["Name","Email","Role","Mobile","Last Login","Status","Actions"].map(c=>(
                    <th key={c} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:600, color:"#718096", letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length===0
                  ? <tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"#718096"}}>No users found</td></tr>
                  : users.map((u,i)=>(
                    <tr key={i} style={{ borderBottom:"1px solid #f1ebe0" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#fdfaf5"}
                      onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <td style={{ padding:"12px 14px", fontWeight:600, color:COLORS.navy }}>{u.full_name}</td>
                      <td style={{ padding:"12px 14px", color:"#4a5568" }}>{u.email}</td>
                      <td style={{ padding:"12px 14px" }}><Badge status={normalizeRole(u.role)} /></td>
                      <td style={{ padding:"12px 14px", color:"#4a5568" }}>{u.mobile||"—"}</td>
                      <td style={{ padding:"12px 14px", color:"#718096", fontSize:12 }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-IN") : "Never"}</td>
                      <td style={{ padding:"12px 14px" }}><Badge status={u.is_active?"Active":"Inactive"} /></td>
                      <td style={{ padding:"12px 14px" }}>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={()=>openEdit(u)}
                            style={{ background:"#dbeafe", color:"#1d4ed8", border:"none", padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                            ✏️ Edit
                          </button>
                          <button onClick={()=>setConfirmDel(u)}
                            style={{ background:"#fee2e2", color:COLORS.danger, border:"none", padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                            🚫 Deactivate
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};



// ─── SHARED HELPERS ───────────────────────────────────────────────────────────





// ─── FORM FIELD COMPONENTS ────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{
      display: "block", fontSize: 11, fontWeight: 600,
      color: "#718096", marginBottom: 5, textTransform: "uppercase",
      letterSpacing: "0.04em"
    }}>
      {label}{required && <span style={{ color: COLORS.danger }}> *</span>}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, type = "text", placeholder, required, min, max }) => (
  <input
    value={value} onChange={e => onChange(e.target.value)}
    type={type} placeholder={placeholder} required={required}
    min={min} max={max}
    style={{
      width: "100%", padding: "9px 12px",
      border: "1px solid #e8e2d6", borderRadius: 8,
      fontSize: 13, fontFamily: "inherit", outline: "none",
      boxSizing: "border-box", background: "#fdfaf5",
      transition: "border-color 0.15s",
    }}
    onFocus={e => e.target.style.borderColor = COLORS.gold}
    onBlur={e => e.target.style.borderColor = "#e8e2d6"}
  />
);

const Select = ({ value, onChange, options, required }) => (
  <select
    value={value} onChange={e => onChange(e.target.value)} required={required}
    style={{
      width: "100%", padding: "9px 12px",
      border: "1px solid #e8e2d6", borderRadius: 8,
      fontSize: 13, fontFamily: "inherit", outline: "none",
      background: "#fdfaf5", boxSizing: "border-box",
    }}
  >
    <option value="">— Select —</option>
    {options.map(o => (
      <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
    ))}
  </select>
);

const FormGrid = ({ cols = 3, children }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 16, marginBottom: 8,
  }}>
    {children}
  </div>
);

const SectionHead = ({ title, icon }) => (
  <div style={{
    fontFamily: "'Playfair Display',serif",
    fontSize: 14, fontWeight: 700, color: COLORS.navy,
    marginBottom: 14, marginTop: 20, paddingBottom: 8,
    borderBottom: `2px solid ${COLORS.gold}`,
    display: "flex", alignItems: "center", gap: 8,
  }}>
    <span>{icon}</span> {title}
  </div>
);

const SubmitBtn = ({ loading, label, icon }) => (
  <button
    type="submit" disabled={loading}
    style={{
      background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.navyLight})`,
      color: COLORS.gold, border: "none",
      padding: "12px 32px", borderRadius: 10,
      fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
      fontFamily: "inherit", letterSpacing: "0.02em",
      opacity: loading ? 0.7 : 1,
    }}
  >
    {loading ? "⏳ Saving..." : `${icon} ${label}`}
  </button>
);

const Alert = ({ msg }) => {
  if (!msg) return null;
  const isOk = msg.startsWith("✅");
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 8, marginBottom: 16,
      fontSize: 13, lineHeight: 1.6,
      background: isOk ? "#f0fdf4" : "#fff5f5",
      color: isOk ? "#15803d" : COLORS.danger,
      border: `1px solid ${isOk ? "#bbf7d0" : "#fecaca"}`,
    }}>
      {msg}
    </div>
  );
};

// ─── COMPANY SELECTOR ─────────────────────────────────────────────────────────
const useCompanies = (token) => {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    // Hardcoded since we seeded these — matches GIPL_Database_Schema.sql
    setCompanies([
      { value: "LIC India",             label: "LIC India" },
      { value: "HDFC Life",             label: "HDFC Life" },
      { value: "ICICI Prudential",      label: "ICICI Prudential Life" },
      { value: "SBI Life",              label: "SBI Life Insurance" },
      { value: "Max Life",              label: "Max Life Insurance" },
      { value: "Star Health",           label: "Star Health & Allied" },
      { value: "Niva Bupa",             label: "Niva Bupa Health" },
      { value: "Care Health",           label: "Care Health Insurance" },
      { value: "Aditya Birla Health",   label: "Aditya Birla Health" },
      { value: "Bajaj Allianz",         label: "Bajaj Allianz General" },
      { value: "TATA AIG",              label: "TATA AIG General" },
      { value: "New India",             label: "New India Assurance" },
      { value: "National Insurance",    label: "National Insurance" },
    ]);
  }, []);
  return companies;
};

// ─── CUSTOMER SEARCH ──────────────────────────────────────────────────────────
const CustomerSearch = ({ token, onSelect, selected }) => {
  const [search, setSearch]     = useState("");
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showList, setShowList] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const data = await apiFetch("GET", `/customers?search=${encodeURIComponent(q)}&limit=8`, null, token);
      if (data.success) setResults(data.data || []);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  if (selected) return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", background: "#f0fdf4",
      border: "1px solid #bbf7d0", borderRadius: 8,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy }}>{selected.full_name}</div>
        <div style={{ fontSize: 12, color: "#718096" }}>{selected.customer_code} · {selected.mobile_primary}</div>
      </div>
      <button onClick={() => onSelect(null)} style={{
        background: "#fee2e2", border: "none", color: COLORS.danger,
        padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
      }}>Change</button>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); setShowList(true); }}
        onFocus={() => setShowList(true)}
        placeholder="🔍 Search by name or mobile number..."
        style={{
          width: "100%", padding: "10px 14px",
          border: "1px solid #e8e2d6", borderRadius: 8,
          fontSize: 13, fontFamily: "inherit", outline: "none",
          background: "#fdfaf5", boxSizing: "border-box",
        }}
      />
      {showList && (search.length >= 2) && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
          background: "#fff", border: "1px solid #e8e2d6", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto",
        }}>
          {loading && <div style={{ padding: "12px 14px", fontSize: 13, color: "#718096" }}>Searching...</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: "12px 14px", fontSize: 13, color: "#718096" }}>
              No customers found. <a href="#" onClick={e => { e.preventDefault(); setShowList(false); }}
                style={{ color: COLORS.gold }}>Add customer first</a>
            </div>
          )}
          {results.map(c => (
            <div key={c.customer_id}
              onClick={() => { onSelect(c); setShowList(false); setSearch(""); }}
              style={{
                padding: "10px 14px", cursor: "pointer",
                borderBottom: "1px solid #f1ebe0",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#fdfaf5"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{c.full_name}</div>
              <div style={{ fontSize: 11, color: "#718096" }}>{c.customer_code} · {c.mobile_primary} · {c.city || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── LIFE POLICY FORM ─────────────────────────────────────────────────────────
const LifePolicyForm = ({ token, onSuccess }) => {
  const companies = useCompanies(token);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");

  const [form, setForm] = useState({
    policy_number: "", company_id: "", policy_type: "Term",
    plan_name: "", sum_assured: "", annual_premium: "",
    payment_frequency: "Annual", payment_term_years: "",
    policy_term_years: "", policy_issue_date: "",
    policy_maturity_date: "", next_premium_due_date: "",
    nominee_name: "", nominee_relation: "", nominee_dob: "",
    first_year_commission_pct: "", renewal_commission_pct: "",
    has_accident_rider: false, has_critical_illness: false,
    notes: "",
  });

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer) { setMsg("❌ Please select a customer first"); return; }
    setSaving(true); setMsg("");
    try {
      const payload = {
        ...form, customer_id: customer.customer_id,
        sum_assured:    parseFloat(form.sum_assured),
        annual_premium: parseFloat(form.annual_premium),
        payment_term_years: form.payment_term_years ? parseInt(form.payment_term_years) : null,
        policy_term_years:  form.policy_term_years  ? parseInt(form.policy_term_years)  : null,
        first_year_commission_pct: form.first_year_commission_pct ? parseFloat(form.first_year_commission_pct) : null,
        renewal_commission_pct:    form.renewal_commission_pct    ? parseFloat(form.renewal_commission_pct)    : null,
      };
      const data = await apiFetch("POST", "/life", payload, token);
      if (data.success) {
        setMsg(`✅ Life policy ${data.data?.policy_number} created successfully!`);
        setForm({ policy_number: "", company_id: "", policy_type: "Term", plan_name: "", sum_assured: "", annual_premium: "", payment_frequency: "Annual", payment_term_years: "", policy_term_years: "", policy_issue_date: "", policy_maturity_date: "", next_premium_due_date: "", nominee_name: "", nominee_relation: "", nominee_dob: "", first_year_commission_pct: "", renewal_commission_pct: "", has_accident_rider: false, has_critical_illness: false, notes: "" });
        setCustomer(null);
        if (onSuccess) onSuccess();
      } else {
        setMsg("❌ " + (data.message || "Failed to create policy"));
      }
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionHead icon="👤" title="Customer" />
      <CustomerSearch token={token} onSelect={setCustomer} selected={customer} />

      <SectionHead icon="📋" title="Policy Details" />
      <FormGrid cols={3}>
        <Field label="Policy Number" required>
          <Input value={form.policy_number} onChange={set("policy_number")} placeholder="e.g. LIC-2024-001" required />
        </Field>
        <Field label="Insurance Company" required>
          <Select value={form.company_id} onChange={set("company_id")} options={companies} required />
        </Field>
        <Field label="Policy Type" required>
          <Select value={form.policy_type} onChange={set("policy_type")} required
            options={["Term","Endowment","ULIP","Whole Life","Money Back","Jeevan","Pension"]} />
        </Field>
        <Field label="Plan Name">
          <Input value={form.plan_name} onChange={set("plan_name")} placeholder="e.g. Jeevan Anand, Click 2 Protect" />
        </Field>
        <Field label="Payment Frequency" required>
          <Select value={form.payment_frequency} onChange={set("payment_frequency")} required
            options={["Annual","Half-Yearly","Quarterly","Monthly","Single"]} />
        </Field>
        <Field label="Policy Term (Years)">
          <Input value={form.policy_term_years} onChange={set("policy_term_years")} type="number" min="1" max="40" placeholder="e.g. 20" />
        </Field>
      </FormGrid>

      <SectionHead icon="💰" title="Financial Details" />
      <FormGrid cols={3}>
        <Field label="Sum Assured (₹)" required>
          <Input value={form.sum_assured} onChange={set("sum_assured")} type="number" min="0" placeholder="e.g. 1500000" required />
        </Field>
        <Field label="Annual Premium (₹)" required>
          <Input value={form.annual_premium} onChange={set("annual_premium")} type="number" min="0" placeholder="e.g. 42800" required />
        </Field>
        <Field label="Premium Paying Term (Years)">
          <Input value={form.payment_term_years} onChange={set("payment_term_years")} type="number" min="1" max="40" placeholder="e.g. 15" />
        </Field>
        <Field label="First Year Commission (%)">
          <Input value={form.first_year_commission_pct} onChange={set("first_year_commission_pct")} type="number" min="0" max="40" placeholder="e.g. 35" />
        </Field>
        <Field label="Renewal Commission (%)">
          <Input value={form.renewal_commission_pct} onChange={set("renewal_commission_pct")} type="number" min="0" max="10" placeholder="e.g. 5" />
        </Field>
      </FormGrid>

      <SectionHead icon="📅" title="Important Dates" />
      <FormGrid cols={3}>
        <Field label="Policy Issue Date" required>
          <Input value={form.policy_issue_date} onChange={set("policy_issue_date")} type="date" required />
        </Field>
        <Field label="Policy Maturity Date">
          <Input value={form.policy_maturity_date} onChange={set("policy_maturity_date")} type="date" />
        </Field>
        <Field label="Next Premium Due Date">
          <Input value={form.next_premium_due_date} onChange={set("next_premium_due_date")} type="date" />
        </Field>
      </FormGrid>

      <SectionHead icon="👨‍👩‍👧" title="Nominee Details" />
      <FormGrid cols={3}>
        <Field label="Nominee Full Name">
          <Input value={form.nominee_name} onChange={set("nominee_name")} placeholder="Full name of nominee" />
        </Field>
        <Field label="Nominee Relation">
          <Select value={form.nominee_relation} onChange={set("nominee_relation")}
            options={["Spouse","Son","Daughter","Father","Mother","Brother","Sister","Other"]} />
        </Field>
        <Field label="Nominee Date of Birth">
          <Input value={form.nominee_dob} onChange={set("nominee_dob")} type="date" />
        </Field>
      </FormGrid>

      <SectionHead icon="🔧" title="Riders & Notes" />
      <div style={{ display: "flex", gap: 24, marginBottom: 14 }}>
        {[["has_accident_rider","Accident Rider"],["has_critical_illness","Critical Illness Rider"]].map(([k,l]) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: COLORS.navy, cursor: "pointer" }}>
            <input type="checkbox" checked={form[k]} onChange={e => set(k)(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: COLORS.gold }} />
            {l}
          </label>
        ))}
      </div>
      <Field label="Notes">
        <textarea value={form.notes} onChange={e => set("notes")(e.target.value)}
          placeholder="Any additional notes about this policy..."
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e2d6", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", minHeight: 70, resize: "vertical" }} />
      </Field>

      <Alert msg={msg} />
      <SubmitBtn loading={saving} label="Save Life Policy" icon="💼" />
    </form>
  );
};

// ─── HEALTH POLICY FORM ───────────────────────────────────────────────────────
const HealthPolicyForm = ({ token, onSuccess }) => {
  const companies = useCompanies(token);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");
  const [members, setMembers]   = useState([{ name: "", relation: "Self", dob: "", gender: "Male" }]);

  const [form, setForm] = useState({
    policy_number: "", company_id: "", policy_type: "Family Floater",
    plan_name: "", sum_insured: "", annual_premium: "",
    payment_frequency: "Annual", policy_issue_date: "",
    policy_start_date: "", policy_expiry_date: "",
    cashless_available: true, tpa_name: "",
    is_ported: false, previous_insurer: "",
    first_year_commission_pct: "", renewal_commission_pct: "",
    notes: "",
  });

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const addMember = () => setMembers(m => [...m, { name: "", relation: "Spouse", dob: "", gender: "Female" }]);
  const removeMember = (i) => setMembers(m => m.filter((_, idx) => idx !== i));
  const updateMember = (i, k, v) => setMembers(m => m.map((mem, idx) => idx === i ? { ...mem, [k]: v } : mem));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer) { setMsg("❌ Please select a customer first"); return; }
    setSaving(true); setMsg("");
    try {
      const payload = {
        ...form, customer_id: customer.customer_id,
        sum_insured:    parseFloat(form.sum_insured),
        annual_premium: parseFloat(form.annual_premium),
        family_members: members,
        policy_start_date:  form.policy_start_date  ? new Date(form.policy_start_date).toISOString()  : null,
        policy_expiry_date: form.policy_expiry_date ? new Date(form.policy_expiry_date).toISOString() : null,
        first_year_commission_pct: form.first_year_commission_pct ? parseFloat(form.first_year_commission_pct) : null,
        renewal_commission_pct:    form.renewal_commission_pct    ? parseFloat(form.renewal_commission_pct)    : null,
      };
      const data = await apiFetch("POST", "/health", payload, token);
      if (data.success) {
        setMsg(`✅ Health policy ${data.data?.policy_number} created successfully!`);
        setCustomer(null);
        setMembers([{ name: "", relation: "Self", dob: "", gender: "Male" }]);
        setForm({ policy_number: "", company_id: "", policy_type: "Family Floater", plan_name: "", sum_insured: "", annual_premium: "", payment_frequency: "Annual", policy_issue_date: "", policy_start_date: "", policy_expiry_date: "", cashless_available: true, tpa_name: "", is_ported: false, previous_insurer: "", first_year_commission_pct: "", renewal_commission_pct: "", notes: "" });
        if (onSuccess) onSuccess();
      } else { setMsg("❌ " + (data.message || "Failed")); }
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionHead icon="👤" title="Customer" />
      <CustomerSearch token={token} onSelect={setCustomer} selected={customer} />

      <SectionHead icon="🏥" title="Policy Details" />
      <FormGrid cols={3}>
        <Field label="Policy Number" required>
          <Input value={form.policy_number} onChange={set("policy_number")} placeholder="e.g. STAR-2024-001" required />
        </Field>
        <Field label="Insurance Company" required>
          <Select value={form.company_id} onChange={set("company_id")} options={companies} required />
        </Field>
        <Field label="Policy Type" required>
          <Select value={form.policy_type} onChange={set("policy_type")} required
            options={["Individual","Family Floater","Senior Citizen","Critical Illness","Group Health","Top-up"]} />
        </Field>
        <Field label="Plan Name">
          <Input value={form.plan_name} onChange={set("plan_name")} placeholder="e.g. Family Health Optima" />
        </Field>
        <Field label="TPA Name">
          <Input value={form.tpa_name} onChange={set("tpa_name")} placeholder="e.g. Medi Assist, Vipul Med" />
        </Field>
        <Field label="Payment Frequency">
          <Select value={form.payment_frequency} onChange={set("payment_frequency")}
            options={["Annual","Half-Yearly","Quarterly","Monthly"]} />
        </Field>
      </FormGrid>

      <SectionHead icon="💰" title="Financial Details" />
      <FormGrid cols={3}>
        <Field label="Sum Insured (₹)" required>
          <Input value={form.sum_insured} onChange={set("sum_insured")} type="number" min="0" placeholder="e.g. 500000" required />
        </Field>
        <Field label="Annual Premium (₹)" required>
          <Input value={form.annual_premium} onChange={set("annual_premium")} type="number" min="0" placeholder="e.g. 24800" required />
        </Field>
        <Field label="First Year Commission (%)">
          <Input value={form.first_year_commission_pct} onChange={set("first_year_commission_pct")} type="number" min="0" max="20" placeholder="e.g. 15" />
        </Field>
        <Field label="Renewal Commission (%)">
          <Input value={form.renewal_commission_pct} onChange={set("renewal_commission_pct")} type="number" min="0" max="10" placeholder="e.g. 7.5" />
        </Field>
      </FormGrid>

      <SectionHead icon="📅" title="Policy Dates" />
      <FormGrid cols={3}>
        <Field label="Issue Date" required>
          <Input value={form.policy_issue_date} onChange={set("policy_issue_date")} type="date" required />
        </Field>
        <Field label="Start Date" required>
          <Input value={form.policy_start_date} onChange={set("policy_start_date")} type="date" required />
        </Field>
        <Field label="Expiry Date" required>
          <Input value={form.policy_expiry_date} onChange={set("policy_expiry_date")} type="date" required />
        </Field>
      </FormGrid>

      <SectionHead icon="👨‍👩‍👧‍👦" title="Family Members Covered" />
      {members.map((m, i) => (
        <div key={i} style={{ background: "#f8f4ed", borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>Member {i + 1}</span>
            {i > 0 && <button type="button" onClick={() => removeMember(i)}
              style={{ background: "#fee2e2", border: "none", color: COLORS.danger, padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Remove</button>}
          </div>
          <FormGrid cols={4}>
            <Field label="Full Name">
              <Input value={m.name} onChange={v => updateMember(i,"name",v)} placeholder="Member name" />
            </Field>
            <Field label="Relation">
              <Select value={m.relation} onChange={v => updateMember(i,"relation",v)}
                options={["Self","Spouse","Son","Daughter","Father","Mother"]} />
            </Field>
            <Field label="Date of Birth">
              <Input value={m.dob} onChange={v => updateMember(i,"dob",v)} type="date" />
            </Field>
            <Field label="Gender">
              <Select value={m.gender} onChange={v => updateMember(i,"gender",v)} options={["Male","Female","Other"]} />
            </Field>
          </FormGrid>
        </div>
      ))}
      <button type="button" onClick={addMember} style={{
        background: "none", border: `1px dashed ${COLORS.gold}`, color: COLORS.gold,
        padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, marginBottom: 16,
      }}>+ Add Family Member</button>

      <SectionHead icon="⚙️" title="Other Details" />
      <FormGrid cols={2}>
        <div style={{ display: "flex", gap: 20 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={form.cashless_available} onChange={e => set("cashless_available")(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: COLORS.gold }} /> Cashless Available
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_ported} onChange={e => set("is_ported")(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: COLORS.gold }} /> Ported Policy
          </label>
        </div>
        {form.is_ported && (
          <Field label="Previous Insurer">
            <Input value={form.previous_insurer} onChange={set("previous_insurer")} placeholder="Previous insurance company" />
          </Field>
        )}
      </FormGrid>
      <Field label="Notes">
        <textarea value={form.notes} onChange={e => set("notes")(e.target.value)}
          placeholder="Pre-existing conditions, special notes..."
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #e8e2d6", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", minHeight: 70, resize: "vertical" }} />
      </Field>

      <Alert msg={msg} />
      <SubmitBtn loading={saving} label="Save Health Policy" icon="🏥" />
    </form>
  );
};

// ─── GENERAL POLICY FORM ──────────────────────────────────────────────────────
const GeneralPolicyForm = ({ token, onSuccess }) => {
  const companies = useCompanies(token);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");

  const [form, setForm] = useState({
    policy_number: "", company_id: "",
    policy_nature: "Motor - Car", policy_sub_nature: "Comprehensive",
    sum_insured: "", annual_premium: "", gst_amount: "",
    policy_issue_date: "", policy_start_date: "", policy_expiry_date: "",
    // Motor fields
    vehicle_registration: "", vehicle_make: "", vehicle_model: "",
    vehicle_year: "", vehicle_type: "Car", fuel_type: "Petrol",
    engine_number: "", chassis_number: "", idv_value: "", ncb_percentage: "0",
    // Property fields
    property_address: "", property_type: "Residential",
    // Common
    previous_policy_number: "", previous_insurer: "",
    first_year_commission_pct: "", renewal_commission_pct: "",
    notes: "",
  });

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const isMotor    = form.policy_nature.startsWith("Motor");
  const isProperty = form.policy_nature.startsWith("Property");
  const isTravel   = form.policy_nature.startsWith("Travel");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer) { setMsg("❌ Please select a customer first"); return; }
    setSaving(true); setMsg("");
    try {
      const payload = {
        ...form, customer_id: customer.customer_id,
        sum_insured:    parseFloat(form.sum_insured),
        annual_premium: parseFloat(form.annual_premium),
        gst_amount:     form.gst_amount ? parseFloat(form.gst_amount) : null,
        total_premium:  parseFloat(form.annual_premium) + (form.gst_amount ? parseFloat(form.gst_amount) : 0),
        idv_value:      form.idv_value ? parseFloat(form.idv_value) : null,
        ncb_percentage: form.ncb_percentage ? parseFloat(form.ncb_percentage) : 0,
        vehicle_year:   form.vehicle_year ? parseInt(form.vehicle_year) : null,
        policy_start_date:  new Date(form.policy_start_date).toISOString(),
        policy_expiry_date: new Date(form.policy_expiry_date).toISOString(),
        first_year_commission_pct: form.first_year_commission_pct ? parseFloat(form.first_year_commission_pct) : null,
        renewal_commission_pct:    form.renewal_commission_pct    ? parseFloat(form.renewal_commission_pct)    : null,
      };
      const data = await apiFetch("POST", "/general", payload, token);
      if (data.success) {
        setMsg(`✅ Policy ${data.data?.policy_number} created successfully!`);
        setCustomer(null);
        setForm({ policy_number:"",company_id:"",policy_nature:"Motor - Car",policy_sub_nature:"Comprehensive",sum_insured:"",annual_premium:"",gst_amount:"",policy_issue_date:"",policy_start_date:"",policy_expiry_date:"",vehicle_registration:"",vehicle_make:"",vehicle_model:"",vehicle_year:"",vehicle_type:"Car",fuel_type:"Petrol",engine_number:"",chassis_number:"",idv_value:"",ncb_percentage:"0",property_address:"",property_type:"Residential",previous_policy_number:"",previous_insurer:"",first_year_commission_pct:"",renewal_commission_pct:"",notes:"" });
        if (onSuccess) onSuccess();
      } else { setMsg("❌ " + (data.message || "Failed")); }
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionHead icon="👤" title="Customer" />
      <CustomerSearch token={token} onSelect={setCustomer} selected={customer} />

      <SectionHead icon="🚗" title="Policy Details" />
      <FormGrid cols={3}>
        <Field label="Policy Number" required>
          <Input value={form.policy_number} onChange={set("policy_number")} placeholder="e.g. BAJAJ-2024-001" required />
        </Field>
        <Field label="Insurance Company" required>
          <Select value={form.company_id} onChange={set("company_id")} options={companies} required />
        </Field>
        <Field label="Policy Nature" required>
          <Select value={form.policy_nature} onChange={set("policy_nature")} required
            options={["Motor - Car","Motor - Two Wheeler","Motor - Commercial","Property - Fire","Property - Burglary","Property - All Risk","Travel - Individual","Travel - Family","Marine - Cargo","Liability"]} />
        </Field>
        <Field label="Sub-Nature / Type" required>
          <Select value={form.policy_sub_nature} onChange={set("policy_sub_nature")} required
            options={isMotor ? ["Comprehensive","Third Party","Own Damage","Zero Depreciation"] : isProperty ? ["Standard Fire & Allied","Earthquake","Flood","All Risk"] : isTravel ? ["Single Trip","Multi Trip","Student"] : ["Standard","Enhanced"]} />
        </Field>
      </FormGrid>

      <SectionHead icon="💰" title="Premium Details" />
      <FormGrid cols={4}>
        <Field label="Sum Insured / IDV (₹)" required>
          <Input value={form.sum_insured} onChange={set("sum_insured")} type="number" min="0" required placeholder="e.g. 850000" />
        </Field>
        <Field label="Net Premium (₹)" required>
          <Input value={form.annual_premium} onChange={set("annual_premium")} type="number" min="0" required placeholder="e.g. 15593" />
        </Field>
        <Field label="GST Amount (₹)">
          <Input value={form.gst_amount} onChange={set("gst_amount")} type="number" min="0" placeholder="e.g. 2807" />
        </Field>
        <Field label="Total Premium (₹)">
          <input readOnly
            value={form.annual_premium && form.gst_amount ? (parseFloat(form.annual_premium) + parseFloat(form.gst_amount)).toFixed(0) : form.annual_premium || ""}
            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", background:"#f0fdf4", boxSizing:"border-box", color:"#15803d", fontWeight:600 }} />
        </Field>
        <Field label="First Year Commission (%)">
          <Input value={form.first_year_commission_pct} onChange={set("first_year_commission_pct")} type="number" min="0" max="20" placeholder="e.g. 12.5" />
        </Field>
        <Field label="Renewal Commission (%)">
          <Input value={form.renewal_commission_pct} onChange={set("renewal_commission_pct")} type="number" min="0" max="15" placeholder="e.g. 5" />
        </Field>
        <Field label="NCB (%)">
          <Select value={form.ncb_percentage} onChange={set("ncb_percentage")} options={["0","20","25","35","45","50"]} />
        </Field>
      </FormGrid>

      <SectionHead icon="📅" title="Policy Dates" />
      <FormGrid cols={3}>
        <Field label="Issue Date" required>
          <Input value={form.policy_issue_date} onChange={set("policy_issue_date")} type="date" required />
        </Field>
        <Field label="Start Date / Risk Date" required>
          <Input value={form.policy_start_date} onChange={set("policy_start_date")} type="date" required />
        </Field>
        <Field label="Expiry Date" required>
          <Input value={form.policy_expiry_date} onChange={set("policy_expiry_date")} type="date" required />
        </Field>
      </FormGrid>

      {isMotor && <>
        <SectionHead icon="🚘" title="Vehicle Details" />
        <FormGrid cols={3}>
          <Field label="Registration Number" required>
            <Input value={form.vehicle_registration} onChange={set("vehicle_registration")} placeholder="e.g. AP 31 AB 1234" required />
          </Field>
          <Field label="Vehicle Make">
            <Input value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="e.g. Maruti Suzuki, Hero" />
          </Field>
          <Field label="Model">
            <Input value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="e.g. Swift Dzire, Splendor" />
          </Field>
          <Field label="Year of Manufacture">
            <Input value={form.vehicle_year} onChange={set("vehicle_year")} type="number" min="1990" max={new Date().getFullYear()} placeholder="e.g. 2022" />
          </Field>
          <Field label="Vehicle Type">
            <Select value={form.vehicle_type} onChange={set("vehicle_type")} options={["Car","Two Wheeler","Auto Rickshaw","Taxi","Bus","Truck","Tractor"]} />
          </Field>
          <Field label="Fuel Type">
            <Select value={form.fuel_type} onChange={set("fuel_type")} options={["Petrol","Diesel","CNG","Electric","LPG","Hybrid"]} />
          </Field>
          <Field label="IDV Value (₹)">
            <Input value={form.idv_value} onChange={set("idv_value")} type="number" min="0" placeholder="Insured Declared Value" />
          </Field>
          <Field label="Engine Number">
            <Input value={form.engine_number} onChange={set("engine_number")} placeholder="Engine number" />
          </Field>
          <Field label="Chassis Number">
            <Input value={form.chassis_number} onChange={set("chassis_number")} placeholder="Chassis / VIN number" />
          </Field>
        </FormGrid>
      </>}

      {isProperty && <>
        <SectionHead icon="🏠" title="Property Details" />
        <FormGrid cols={2}>
          <Field label="Property Type">
            <Select value={form.property_type} onChange={set("property_type")} options={["Residential","Commercial","Industrial","Warehouse"]} />
          </Field>
          <Field label="Property Address">
            <Input value={form.property_address} onChange={set("property_address")} placeholder="Full address of property" />
          </Field>
        </FormGrid>
      </>}

      <SectionHead icon="📝" title="Previous Policy" />
      <FormGrid cols={2}>
        <Field label="Previous Policy Number">
          <Input value={form.previous_policy_number} onChange={set("previous_policy_number")} placeholder="If renewal" />
        </Field>
        <Field label="Previous Insurer">
          <Input value={form.previous_insurer} onChange={set("previous_insurer")} placeholder="Previous insurance company" />
        </Field>
      </FormGrid>

      <Field label="Notes">
        <textarea value={form.notes} onChange={e => set("notes")(e.target.value)}
          placeholder="Special conditions, add-ons, endorsements..."
          style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", minHeight:70, resize:"vertical" }} />
      </Field>

      <Alert msg={msg} />
      <SubmitBtn loading={saving} label="Save General Policy" icon="🚗" />
    </form>
  );
};

// ─── POLICY FORMS CONTAINER ───────────────────────────────────────────────────
const PolicyFormsModule = ({ token }) => {
  const [activeTab, setActiveTab] = useState("life");
  const [successCount, setSuccessCount] = useState(0);

  const tabs = [
    { id: "life",    icon: "💼", label: "Life Insurance" },
    { id: "health",  icon: "🏥", label: "Health Insurance" },
    { id: "general", icon: "🚗", label: "General Insurance" },
  ];

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: "10px 22px", borderRadius: 10, border: "1px solid #e8e2d6",
              background: activeTab === t.id
                ? `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.navyLight})`
                : "#fff",
              color: activeTab === t.id ? COLORS.gold : "#4a5568",
              fontSize: 14, fontWeight: activeTab === t.id ? 600 : 400,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
              transition: "all 0.15s",
            }}>
            {t.icon} {t.label}
          </button>
        ))}
        {successCount > 0 && (
          <div style={{ marginLeft: "auto", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#15803d", fontWeight: 600 }}>
            ✅ {successCount} {successCount === 1 ? "policy" : "policies"} added today
          </div>
        )}
      </div>

      {/* Form Card */}
      <div style={{ background: "#fff", border: "1px solid #e8e2d6", borderRadius: 16, padding: 28 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, color: COLORS.navy, marginBottom: 4 }}>
          {tabs.find(t => t.id === activeTab)?.icon} Add New {tabs.find(t => t.id === activeTab)?.label} Policy
        </div>
        <div style={{ fontSize: 13, color: "#718096", marginBottom: 20 }}>
          Fill all required fields marked with <span style={{ color: COLORS.danger }}>*</span>. Policy is saved directly to the database.
        </div>

        {activeTab === "life"    && <LifePolicyForm    token={token} onSuccess={() => setSuccessCount(c => c+1)} />}
        {activeTab === "health"  && <HealthPolicyForm  token={token} onSuccess={() => setSuccessCount(c => c+1)} />}
        {activeTab === "general" && <GeneralPolicyForm token={token} onSuccess={() => setSuccessCount(c => c+1)} />}
      </div>
    </div>
  );
};


// ─── NAV CONFIG ───────────────────────────────────────────────────────────────
const NAV = [
  { id: "dashboard",  icon: "📊", label: "Dashboard" },
  { id: "customers",  icon: "👥", label: "Customers" },
  { id: "life",       icon: "💼", label: "Life Policies" },
  { id: "health",     icon: "🏥", label: "Health Policies" },
  { id: "general",    icon: "🚗", label: "General Insurance" },
  { id: "agents",     icon: "🧑‍💼", label: "Agents & Leads" },
  { id: "reminders",  icon: "🔔", label: "Reminders" },
  { id: "reports",    icon: "📈", label: "Reports" },
  { id: "users",      icon: "👤", label: "Users" },
  { id: "addpolicy",  icon: "➕", label: "Add Policy" },
];

const TITLES = {
  dashboard: "Dashboard Overview", customers: "Customer Database",
  life: "Life Insurance Policies", health: "Health Insurance Policies",
  general: "General Insurance", agents: "Agents & Lead Generators",
  reminders: "Renewal Reminders", reports: "Reports & Analytics",
  users: "System Users",
  addpolicy: "Add New Policy",
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken]     = useState(globalToken);
  const [user, setUser]       = useState(globalUser);
  const [active, setActive]   = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);

  const handleLogin = (t, u) => { setToken(t); setUser(u); globalToken = t; globalUser = u; };

  const handleLogout = () => {
    localStorage.removeItem("gipl_token"); localStorage.removeItem("gipl_user");
    setToken(""); setUser(null); globalToken = ""; globalUser = null;
  };

  if (!token) return <LoginPage onLogin={handleLogin} />;

  const renderModule = () => {
    switch (active) {
      case "dashboard": return <DashboardModule token={token} />;
      case "customers": return <CustomersModule token={token} />;
      case "life":      return <PoliciesModule token={token} type="life" />;
      case "health":    return <PoliciesModule token={token} type="health" />;
      case "general":   return <PoliciesModule token={token} type="general" />;
      case "agents":    return <AgentsModule token={token} />;
      case "reminders": return <RemindersModule token={token} />;
      case "reports":   return <ReportsModule token={token} />;
      case "users":     return <UsersModule token={token} />;
      case "addpolicy": return <PolicyFormsModule token={token} />;
      default:          return <EmptyState icon="🚧" title="Coming soon" sub="This module is under development" />;
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'DM Sans',sans-serif", background: "#f8f4ed", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* SIDEBAR */}
      <div style={{ width: collapsed ? 64 : 210, background: COLORS.navy, display: "flex", flexDirection: "column", transition: "width 0.25s", flexShrink: 0, overflow: "hidden" }}>
        <div style={{ padding: "18px 14px", borderBottom: "1px solid rgba(201,168,76,0.15)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, background: `linear-gradient(135deg,${COLORS.gold},${COLORS.goldLight})`, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',serif", fontWeight: 700, color: COLORS.navy, fontSize: 15, flexShrink: 0 }}>G</div>
          {!collapsed && <div><div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1 }}>GIPL</div><div style={{ fontSize: 8, color: COLORS.gold, letterSpacing: "0.12em", textTransform: "uppercase" }}>Insurance Partners</div></div>}
        </div>
        <nav style={{ flex: 1, padding: "10px 7px", overflowY: "auto" }}>
          {NAV.map(item => (
            <button key={item.id} onClick={() => setActive(item.id)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: collapsed ? "10px 14px" : "9px 11px", justifyContent: collapsed ? "center" : "flex-start", borderRadius: 8, border: "none", cursor: "pointer", background: active === item.id ? "rgba(201,168,76,0.12)" : "transparent", color: active === item.id ? COLORS.gold : "rgba(255,255,255,0.45)", fontSize: 13, fontFamily: "inherit", fontWeight: active === item.id ? 600 : 400, marginBottom: 2, width: "100%" }}>
              <span style={{ fontSize: 17, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 10px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg,${COLORS.gold},${COLORS.goldLight})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',serif", fontWeight: 700, color: COLORS.navy, fontSize: 11, flexShrink: 0 }}>
            {user?.name?.charAt(0) || "U"}
          </div>
          {!collapsed && <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
            <div style={{ fontSize: 10, color: COLORS.gold }}>{normalizeRole(user?.role)}</div>
          </div>}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e8e2d6", padding: "0 22px", height: 58, display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <button onClick={() => setCollapsed(!collapsed)}
            style={{ background: "none", border: "1px solid #e8e2d6", borderRadius: 7, width: 32, height: 32, cursor: "pointer", fontSize: 15, color: "#718096", flexShrink: 0 }}>☰</button>
          <div style={{ flex: 1, fontFamily: "'Playfair Display',serif", fontSize: 17, fontWeight: 700, color: COLORS.navy }}>{TITLES[active]}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#718096", background: "#f8f4ed", padding: "4px 10px", borderRadius: 20 }}>🟢 Live Data</div>
            <button onClick={handleLogout}
              style={{ background: "#fff5f5", border: "1px solid #fecaca", color: COLORS.danger, padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
              Sign Out
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>
          {renderModule()}
        </div>
      </div>
    </div>
  );
}
